import { useState } from "react";
import {
  CheckCheck,
  ChevronRight,
  Circle,
  Clock3,
  Inbox,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Plus,
  Terminal,
} from "lucide-react";
import type { Task, Lane } from "../../../server/src/models";
import { ago } from "../api";
import { laneColor, IconButton, Priority } from "./ui";

export function LaneColumn({
  lane,
  tasks,
  onOpen,
  onNew,
  onTag,
  onDrop,
  onCollapse,
}: {
  lane: Lane;
  tasks: Task[];
  onOpen: (id: string) => void;
  onNew: () => void;
  onTag: (tag: string) => void;
  onDrop: (id: string) => void;
  onCollapse: () => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <section
      aria-label={`${lane.title} lane`}
      className={`lane ${lane.collapsed ? "collapsed" : ""} ${over ? "drag-over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDrop(e.dataTransfer.getData("text/plain"));
      }}
    >
      <header className="lane-header">
        <button className="lane-title" onClick={onCollapse}>
          <span className={`dot ${laneColor(lane.key)}`} />
          <span>{lane.title}</span>
          <span className="count">{tasks.length}</span>
        </button>
        <div className="lane-tools">
          <IconButton label={`Add task to ${lane.title}`} onClick={onNew}>
            <Plus size={15} />
          </IconButton>
          <IconButton
            label={`${lane.collapsed ? "Expand" : "Collapse"} ${lane.title}`}
            onClick={onCollapse}
          >
            {lane.collapsed ? (
              <ChevronRight size={14} />
            ) : (
              <MoreHorizontal size={16} />
            )}
          </IconButton>
        </div>
      </header>
      {!lane.collapsed && (
        <>
          <div className="lane-content">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onOpen={() => onOpen(task.id)}
                onTag={onTag}
              />
            ))}
            {!tasks.length && (
              <div className="lane-empty">
                <span className={`empty-mark ${laneColor(lane.key)}`}>
                  {lane.key === "done" ? (
                    <CheckCheck size={19} />
                  ) : lane.key === "wait_user" ? (
                    <Inbox size={19} />
                  ) : (
                    <Circle size={17} />
                  )}
                </span>
                <span>
                  {lane.key === "done"
                    ? "Good work ends here."
                    : lane.key === "wait_user"
                      ? "Nothing waiting on you."
                      : lane.key === "in_progress"
                        ? "Room for the next run."
                        : "A little room to think."}
                </span>
              </div>
            )}
          </div>
          <button className="add-task" onClick={onNew}>
            <Plus size={14} />
            Add task
          </button>
        </>
      )}
    </section>
  );
}
function TaskCard({
  task,
  onOpen,
  onTag,
}: {
  task: Task;
  onOpen: () => void;
  onTag: (tag: string) => void;
}) {
  return (
    <article
      className={`task-card ${task.current_run ? (task.current_run.stale ? "run-stale" : "run-active") : ""} ${task.needs_human ? "needs-attention" : ""}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", task.id);
        e.dataTransfer.effectAllowed = "move";
      }}
    >
      <button
        className="card-main"
        draggable
        onClick={onOpen}
        aria-label={`${task.id}: ${task.title}`}
      >
        <div className="card-top">
          <span className="mono task-id">{task.id}</span>
          <div>
            {task.current_run && (
              <span
                className={`run-pill ${task.current_run.stale ? "stale" : ""}`}
              >
                <span
                  className={`dot ${task.current_run.stale ? "amber" : "green"}`}
                />
                {task.current_run.stale ? "Stale" : "Running"}
              </span>
            )}
            <Priority value={task.priority} />
          </div>
        </div>
        <h3>{task.title}</h3>
        {task.description && (
          <p className="card-description">{task.description}</p>
        )}
      </button>
      {task.tags.length > 0 && (
        <div className="card-tags">
          {task.tags.slice(0, 3).map((tag) => (
            <button key={tag} onClick={() => onTag(tag)}>
              #{tag}
            </button>
          ))}
          {task.tags.length > 3 && <span>+{task.tags.length - 3}</span>}
        </div>
      )}
      {task.needs_human && (
        <button className="card-attention" onClick={onOpen}>
          <Inbox size={13} />
          {task.lane === "wait_user"
            ? "Ready for your review"
            : "A question needs your input"}
          <ChevronRight size={13} />
        </button>
      )}
      <div className="card-divider" />
      <button className="card-bottom" onClick={onOpen}>
        <div>
          {task.current_run ? (
            <>
              <span className="agent-avatar">
                <Terminal size={10} />
              </span>
              <span className="card-holder">
                {task.current_run.session_name.split(":").slice(1).join(":")}
              </span>
            </>
          ) : task.handoff ? (
            <>
              <span className="dot green" />
              <span>Handoff saved</span>
            </>
          ) : (
            <>
              <span className="unclaimed-dot" />
              <span>No active run</span>
            </>
          )}
        </div>
        <span className="card-meta">
          {task.attachment_count > 0 && (
            <span>
              <Paperclip size={11} />
              {task.attachment_count}
            </span>
          )}
          {task.comment_count > 0 && (
            <span>
              <MessageSquare size={11} />
              {task.comment_count}
            </span>
          )}
          <span>
            <Clock3 size={11} />
            {ago(task.lane_entered_at)}
          </span>
        </span>
      </button>
    </article>
  );
}
